import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Heading,
  Box,
  Center,
  Flex,
  Link,
  Icon,
  HStack,
  Text,
  Button,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

import { FiCalendar, FiMapPin, FiDollarSign } from "react-icons/fi";

import { getDisplaySalary } from "../../helpers/strings";
import { PlainLayout } from "../../layouts/AppLayout";
import { GET_WORKER_FOR_REVIEW_QUERY, SUBMIT_WORKER_REVIEW_MUTATION } from "../../graphql/worker_review";
import WithQuery from "../../components/WithQuery";
import StarRating from "../../components/StarRating";
import MoblyzeApolloClient from "../../services/MoblyzeApolloClient";
import * as Sentry from "@sentry/react";

import { DASHBOARD_PATH, LOGIN_PATH } from "../../routes";

function WorkerReview({ data, error, loading, refetch, token }) {
  const [rating, setRating] = useState(0); // State for star rating
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const onSubmitButtonPress = () => {
    setIsSubmitting(true);

    MoblyzeApolloClient.mutate({
      mutation: SUBMIT_WORKER_REVIEW_MUTATION,
      variables: { token: token, score: rating },
    })
      .then((r) => {
        setIsSubmitted(true);
      })
      .catch((e) => {
        Sentry.captureException(e);
        throw e;
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (loading) {
    return <PlainLayout />;
  }

  if (!token || error) {
    return (
      <PlainLayout>
        <Center flex="1">
          <Flex
            px={{ base: "4", md: "8" }}
            py={{ base: "12", md: "48" }}
            width="full"
            maxW="md"
            direction="column"
            align="center"
            textAlign={"center"}
          >
            <Box
              fontSize="3xl"
              fontWeight="bold"
              lineHeight="tight"
              textAlign="center"
              mb="8"
            >
              Invalid link
            </Box>
            <Box fontSize="lg" lineHeight="tight" textAlign="center" mb="8">
              Your invite link may have expired, or have already been consumed.
              <br />
              <br />
              Please check within your organisation.
            </Box>

            <Link
              fontSize="sm"
              mt={10}
              color="colors.white"
              textDecoration={"underline"}
              textDecorationColor={"colors.white"}
              href={LOGIN_PATH}
            >
              Already have an account? Login
            </Link>
          </Flex>
        </Center>
      </PlainLayout>
    );
  }

  const worker = data.getWorkerForReview;

  return (
    <PlainLayout>
      <Center width="100%" height="100%">
        <Flex flexDirection="column" w="100%" maxWidth="680px"
          padding={{base: "16px", sm: "32px"}}
          gap={{base: "16px", sm: "32px"}}
          bgColor="#171717"
          borderWidth="1px"
          borderRadius="12px"
          borderColor="whiteAlpha.600">
          {!isSubmitted ? (
            <Heading fontSize="36px" lineHeight="120%">How did {worker.name} perform in their role as {worker.role}?</Heading>
          ) : (
            <>
              <Heading fontSize="36px" lineHeight="120%"><CheckCircleIcon fontSize={'36px'} color="green.300" marginRight="12px" />Thank you!</Heading>
              <Text fontSize="24px">Your review for {worker.name} has been successfully submitted.</Text>
            </>
          )}
          <Flex>
            <Box
              height="112px"
              width="112px"
              borderRadius="50%"
              background={`center / cover no-repeat url(${worker?.profileImageUrl ||
                "https://ui-avatars.com/api/?name=" +
                worker?.name.replace(/ /g, "+") +
                "&background=random&rounded=true"
                })`}
              alt="Worker"
            />
            <Flex height="112px" flexDirection="column" justifyContent="space-around" ml="24px">
              <HStack>
                <Icon as={FiCalendar} fontSize={24} />
                <Text>
                  {`Started ${moment(worker?.startDate).format("MMMM Do, YYYY")}`}
                </Text>
              </HStack>
              <HStack>
                <Icon as={FiMapPin} fontSize={24} />
                <Text>{worker.location}</Text>
              </HStack>
              <HStack>
                <Icon as={FiDollarSign} fontSize={24} />
                <Text>{getDisplaySalary(worker)}</Text>
              </HStack>
            </Flex>
          </Flex>
          {!isSubmitted && (
            <>
              <Flex>
                <StarRating rating={rating} setRating={setRating} />
              </Flex>
              <Box fontSize="20px">Select stars to rate. Once rated for this job, your rating cannot be changed.</Box>
            </>
          )}
          <Box height="1px" width="100%" backgroundColor="whiteAlpha.400"></Box>
          {!isSubmitted ? (
            <Button variant='roundedCorner' fontSize="18px"
              isLoading={isSubmitting} loadingText="Submitting..."
              onClick={onSubmitButtonPress} isDisabled={!rating}>Submit rating</Button>
          ) : (
            <Button variant='roundedCorner' fontSize="18px"
              onClick={() => navigate(DASHBOARD_PATH) }>Back to Moblyze</Button>
          )}
        </Flex>
      </Center>
    </PlainLayout>
  );
}

function PageComponent({ children }) {
  // Get token from url query params

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");

  return WithQuery(
    WorkerReview,
    GET_WORKER_FOR_REVIEW_QUERY,
    { token: token },
    {
      children,
      token,
    }
  )();
}

export default PageComponent;
